import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * Card with a clickable header strip: icon + title + status summary,
 * chevron right. The body mounts only while open.
 *
 * This is the panel recipe the feasibility check established: every
 * Step-2 panel says its state in ONE line (badge in the header) and
 * expands on demand — so the page reads as a stack of statuses, not a
 * wall of equally tall boxes.
 */
export function CollapsibleCard({
  icon,
  title,
  summary,
  defaultOpen = false,
  className,
  children,
}: {
  icon?: ReactNode;
  title: ReactNode;
  /** Status content for the header strip — badges, counts, hints. */
  summary?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card className={className}>
      <CardHeader>
        <button
          type="button"
          className="flex w-full items-center gap-2 text-left"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              {icon}
              {title}
            </span>
          </CardTitle>
          {summary && (
            <span className="ml-2 flex flex-wrap items-center gap-2 text-[0.78rem] text-stone-500">
              {summary}
            </span>
          )}
          <ChevronDown
            className={cn(
              "ml-auto h-4 w-4 flex-shrink-0 text-stone-400 transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </CardHeader>
      {open && <CardBody>{children}</CardBody>}
    </Card>
  );
}
